<template>
  <div>
    <data-table
      ref="table"
      :fetch-from="fetchFrom"
      :fields="fields"
      @loaded="onTableLoaded"
    >
      <template
        slot="actions"
        slot-scope="slotData"
      >
        <template v-if="!isReadOnlyLocal()">
          <v-btn
            x-small
            color="primary"
            @click="showEdit(slotData.rowData)"
          >Edit</v-btn>
        </template>&nbsp;
        <template>
          <v-btn
            x-small
            color="primary"
            @click="showDetails(slotData.rowData)"
          >Details</v-btn>
        </template>
      </template>

    </data-table>
    <v-btn
      v-if="!isReadOnlyLocal()"
      color="primary"
      class="mr-4"
      small
      @click="showAddDialog()"
    >
      Add new
    </v-btn>
    <empty-space />
    <modal-form
      :title="title"
      :show.sync="dialog"
      :readonly="isReadOnlyLocalModal()"
      v-on:saveData="doSave"
      :has-subtitle="true"
    >
      <template v-slot:subtitle>
        <v-row>
          <v-col cols="4">
            <display-field label="Site">
              {{ site.site_id_display }} {{ site.site_name }}
            </display-field>
          </v-col>

          <v-col cols="4">
            <display-field label="Active candidate">{{ site.active_candidate|formatBool }}</display-field>
          </v-col>
        </v-row>
      </template>
      <v-form v-model="valid">
        <v-row>
          <v-col cols="4">
            <type-permit
              :readonly="isReadOnlyLocalModal()"
              :value="getValue(permit, 'type_of_permit')"
              @input="setValue(permit, 'type_of_permit', $event)"
              label="Type of permit"
            ></type-permit>
          </v-col>
          <v-col cols="4">
            <permit-status
              :readonly="isReadOnlyLocalModal()"
              :value="getValue(permit, 'permit_status')"
              @input="setValue(permit, 'permit_status', $event)"
              label="Permit status"
            ></permit-status>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4">
            <date-picker
              :readonly="isReadOnlyLocalModal()"
              label="Requested date"
              :value="getValue(permit, 'requested_date')"
              @input="setValue(permit, 'requested_date', $event)"
            />
          </v-col>
          <v-col cols="4">
            <date-picker
              :readonly="isReadOnlyLocalModal()"
              label="Received date"
              :value="getValue(permit, 'received_date')"
              @input="setValue(permit, 'received_date', $event)"
            />
          </v-col>
          <v-col cols="4">
            <date-picker
              :readonly="isReadOnlyLocalModal()"
              label="Valid to"
              :value="getValue(permit, 'valid_to')"
              @input="setValue(permit, 'valid_to', $event)"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4">
            <text-field
              :readonly="isReadOnlyLocalModal()"
              label="Class number"
              :value="getValue(permit, 'class_number')"
              @input="setValue(permit, 'class_number', $event)"
            ></text-field>
          </v-col>
          <v-col cols="4">
            <s-textarea
              :readonly="isReadOnlyLocalModal()"
              label="Comment"
              :value="getValue(permit, 'comment')"
              @input="setValue(permit, 'comment', $event)"
            ></s-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <h2>Documents</h2>
            <file-upload
              :readonly="isReadOnlyLocalModal()"
              module="permit_ministry_of_health"
              :site_id="site.site_id"
              label="Documents"
              :multi="true"
              :item_id="permit.id"
              @input="setValue(permit, 'files', $event)"
            >
            </file-upload>
          </v-col>
        </v-row>
      </v-form>
    </modal-form>
  </div>
</template>

<script>
  import TypePermit from '@/components/codelist/TypePermit'
  import DisplayField from '@/components/DisplayField'
  import PermitStatus from '@/components/codelist/PermitStatus'
  import DatePicker from '@/components/DatePicker'
  import TextField from '@/components/TextField'
  import STextarea from '@/components/Textarea'
  import FileUpload from '@/components/FileUpload'
  import FormActions from '@/components/FormActions'
  import ReactivityMixin from '@/mixins/Reactivity'
  import PermissionsMixin from '@/mixins/Permissions'
  import ModalForm from '@/components/ModalForm'
  import DataTable from '@/components/DataTable'
  import SitePermitMinistryHealthService from '@/services/site-permit-ministry-health.vue'
  export default {
    name: 'Permits',
    components: { DataTable, ModalForm, FormActions, FileUpload, STextarea, TextField, DatePicker, PermitStatus, DisplayField, TypePermit },
    mixins: [
      ReactivityMixin,
      PermissionsMixin,
    ],
    props: {
      site: {
        default: undefined,
      },
      autoOpen: {
        default: null,
      },
    },
    data: () => ({
      loading: false,
      permit: {},
      section: 'permit_ministry_of_health',
      dialog: false,
      valid: false,
      fields: [
        {
          name: 'type_of_permit_name',
          title: 'Type of permit',
        },
        {
          name: 'permit_status_name',
          title: 'Permit status',
        },
        {
          name: 'requested_date',
          title: 'Requested date',
        },
        {
          name: 'received_date',
          title: 'Received date',
        },
        {
          name: 'valid_to',
          title: 'Valid to',
        },
        {
          name: 'class_number',
          title: 'Class number',
        },
        {
          name: '__slot:actions',
          title: 'Actions',
        },
      ],
      wasAutoLoaded: false,
      showOnlyDetails: false,
    }),
    computed: {
      title () {
        if (this.isReadOnlyLocalModal()) {
          return 'Permit details'
        }
        if (this.isEditing) {
          return 'Edit permit'
        }
        return 'Add permit'
      },
      isEditing () {
        return this.permit.id !== undefined
      },
    },
    methods: {
      isReadOnlyLocal () {
        return this.isReadOnly() || this.forceReadOnly
      },
      isReadOnlyLocalModal () {
        return this.isReadOnlyLocal() || this.showOnlyDetails
      },
      fetchFrom (urls, options) {
        const dcs = new SitePermitMinistryHealthService(this.site.id)
        return dcs.list(options.params)
      },
      showAddDialog () {
        this.showOnlyDetails = false
        this.permit = {}
        this.dialog = true
      },
      showEdit (item) {
        this.showOnlyDetails = false
        this.permit = JSON.parse(JSON.stringify(item))
        this.dialog = true
      },
      showDetails (item) {
        this.showOnlyDetails = true
        this.permit = JSON.parse(JSON.stringify(item))
        this.dialog = true
      },
      doSave () {
        if (this.valid) {
          this.loading = true
          if (this.isEditing) {
            const ls = new SitePermitMinistryHealthService(this.site.id)
            ls.put(this.permit.id, this.permit).then((response) => {
              this.dialog = false
              this.$refs.table.refresh()
            }).finally(() => {
              this.loading = false
            })
          } else {
            const ls = new SitePermitMinistryHealthService(this.site.id)
            ls.post(this.permit).then((response) => {
              this.dialog = false
              this.$refs.table.refresh()
            }).finally(() => {
              this.loading = false
            })
          }
        }
      },
      onTableLoaded () {
        if (this.autoOpen != null && !this.wasAutoLoaded) {
          const data = this.$refs.table.getData()
          for (const item of data) {
            if (item.id + '' === this.autoOpen) {
              setTimeout(() => {
                this.wasAutoLoaded = true
                this.showDetails(item)
              }, 200)
              break
            }
          }
        }
      },
    },
  }
</script>

<style scoped>

</style>
